import {createContext, type Dispatch} from 'react'
import type {Action as AssetListAction} from './state/asset-list/actions'
import type {Action as AssetFormAction} from './state/asset-form/actions'
import type {Action as AssetFormOptionsAction} from './state/asset-form-options/actions'
import type {Action as PageAction} from './state/page/actions'
import type {Action as AssetUploadModalAction} from './state/asset-upload-modal/actions'
import type {Action as OTSystemsAction} from './state/ot-systems/actions'
import type {Action as SystemAssessmentModalAction} from './state/system-assessment-modal/actions'
import LoadingState from '../../../values/loading-state-enum'
import type {DropdownOption} from '../components/dropdown'
import {ProcessingStatus} from '@/values/processing-save-state-enum.ts'
import {type AssetBulkFile, type OTSystemMap} from './types/ot-types'
import {type GuidType} from '@/values/generic-type-defintions.ts'
import {DefaultAssetStatusDropdownOption} from '../components/asset-status'
import {AssetStatusToLabelMap, OTAssetLifecycleStatusEnum} from '@/store/state/ot-assets/state.ts'
import type {NetworkAsset} from '@/api/network-assets/types/network-assets.types'
import type {
    AssetNetworkType,
    BlockStatus,
} from '@/pages/network-assets/context/types/aggregation-state'

export const OT_ASSET_DEFAULT_LIFECYCLE_STATUSES = Object.values(OTAssetLifecycleStatusEnum).map(
    (status, id) => ({
        name: AssetStatusToLabelMap[status],
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        id: id + '',
        created_at: '',
        updated_at: '',
    }),
)

export interface AssetList {
    state: {
        // Probably not efficient to store it this way,
        // but i don't foresee user will expand so many
        // rows. Storing as object would be O(1) as opposed to O(n)
        // when trying to access it.
        expandedAssetId: string[]
    }
    dispatch: Dispatch<AssetListAction> | undefined
}

export interface AssetOption {
    id: string
    name: string
    created_at?: string
    updated_at?: string
    // Only present in software table for now
    group_name?: string
}

export type FormStringField = keyof Pick<
    AssetForm['state']['formData'],
    'name' | 'vendor' | 'model' | 'description' | 'softwareVersion' | 'ipAddress' | 'macAddress'
>

export type FormDropdownField = keyof Pick<
    AssetForm['state']['formData'],
    | 'assetTypeID'
    | 'systemID'
    | 'zoneID'
    | 'softwareID'
    | 'communicationProtocolID'
    | 'status'
    | 'owner'
>

export type MultiselectField = 'physicalInterfaceIDs'

export interface AssetForm {
    state: {
        formData: {
            name: string
            assetTypeID: DropdownOption
            systemID: DropdownOption
            zoneID: DropdownOption
            owner: DropdownOption
            vendor: string
            status: DropdownOption
            model: string
            description: string
            physicalInterfaceIDs: DropdownOption[]
            softwareID: DropdownOption
            softwareVersion: string
            communicationProtocolID: DropdownOption
            customFields: {name: string; value: string}[]
            ipAddress: string
            macAddress: string
            source: string
        }
        formFieldError: {
            [key: string]: string | {name: string; value: string}[]
            assetTypeID: string
            communicationProtocolID: string
            description: string
            model: string
            name: string
            owner: string
            physicalInterfaceIDs: string
            softwareID: string
            softwareVersion: string
            systemID: string
            vendor: string
            zoneID: string
            customFields: {name: string; value: string}[]
            ipAddress: string
            macAddress: string
            source: string
        }
        formSubmitLoadingState: LoadingState
        formSubmitError: string
        // Id of asset being edited
        editingAssetId: string | null
        editingAssetIdRevision: number | null
    }
    dispatch: Dispatch<AssetFormAction> | undefined
}

export enum AssetOptionKeyEnum {
    SOFTWARE = 'softwares',
    ZONE = 'zones',
    SYSTEM = 'systems',
    PHYSICAL_INTERFACE = 'physicalInterfaces',
    COMMUNICATION_PROTOCOL = 'communicationProtocols',
    ASSET_TYPE = 'assetTypes',
    LIFECYCLE_STATUS = 'lifecycleStatuses',
    USERS = 'users',
}

export interface AssetFormOptions {
    state: {
        formOptionsLoadingState: LoadingState
        formOptions: AssetOptions
        formFieldError: {
            name: string
        }
        formSubmitError: string
        formSubmitLoadingState: LoadingState
    }
    dispatch: Dispatch<AssetFormOptionsAction> | undefined
}

export interface Page {
    state: {
        // Which location are we currently viewing
        locationID: string | null
        isOpenAssetCreationForm: boolean
        isDisabledAddButton: boolean
    }
    dispatch: Dispatch<PageAction> | undefined
}

export interface AssetUploadModal {
    state: {
        uploadingStatus: ProcessingStatus
        file: File | null
        // From server
        serverMessage: ServerMessage
        isOpen: boolean
        // List of files uploaded and possibly what is being uploaded.
        // TODO: Change interface to match what the server returns
        files: Array<AssetBulkFile | Partial<AssetBulkFile>>
        fetchLoadingState: LoadingState
        selectedLiveAssets: NetworkAsset[]
    }
    dispatch: Dispatch<AssetUploadModalAction> | undefined
}

export interface ServerMessage {
    type: 'error' | 'success' | null
    message: string
}

interface LocationInventoryPageContext {
    assetList: AssetList
    assetForm: AssetForm
    assetFormOptions: AssetFormOptions
    page: Page
    assetUploadModal: AssetUploadModal
    otSystems: OTSystems
    systemAssessmentModal: SystemAssessmentModal
}

export interface OTSystems {
    state: {
        data: OTSystemMap
        loadingState: LoadingState
        expandedSystemNames: string[]
        reportUrl: string | null
        showGenerateReportPopup: boolean
        createReportError: string
        isErrorOpen: boolean
    }
    dispatch: Dispatch<OTSystemsAction> | undefined
}

export interface SystemAssessmentModal {
    state: {
        isOpen: boolean
        systemID?: GuidType
        submissionState: LoadingState
        error?: string
    }
    dispatch: Dispatch<SystemAssessmentModalAction> | undefined
}

export const defaultLocationInventoryContextState: LocationInventoryPageContext = {
    assetList: {
        state: {
            expandedAssetId: [],
        },
        dispatch: undefined,
    },
    assetForm: {
        state: {
            formData: {
                name: '',
                assetTypeID: {value: '', label: ''},
                systemID: {value: '', label: ''},
                zoneID: {value: '', label: ''},
                owner: {value: '', label: ''},
                vendor: '',
                status: DefaultAssetStatusDropdownOption,
                model: '',
                description: '',
                physicalInterfaceIDs: [],
                softwareID: {value: '', label: ''},
                softwareVersion: '',
                communicationProtocolID: {value: '', label: ''},
                customFields: [],
                ipAddress: '',
                macAddress: '',
                source: 'otInventory',
            },
            formFieldError: {
                assetTypeID: '',
                communicationProtocolID: '',
                description: '',
                owner: '',
                model: '',
                name: '',
                physicalInterfaceIDs: '',
                softwareID: '',
                softwareVersion: '',
                systemID: '',
                vendor: '',
                status: '',
                zoneID: '',
                customFields: [],
                ipAddress: '',
                macAddress: '',
                source: 'otInventory',
            },
            formSubmitLoadingState: LoadingState.NotPopulated,
            formSubmitError: '',
            editingAssetId: null,
            editingAssetIdRevision: null,
        },
        dispatch: undefined,
    },

    assetFormOptions: {
        state: {
            formOptionsLoadingState: LoadingState.NotPopulated,
            formOptions: {
                [AssetOptionKeyEnum.SOFTWARE]: [],
                [AssetOptionKeyEnum.SYSTEM]: [],
                [AssetOptionKeyEnum.ZONE]: [],
                [AssetOptionKeyEnum.PHYSICAL_INTERFACE]: [],
                [AssetOptionKeyEnum.COMMUNICATION_PROTOCOL]: [],
                [AssetOptionKeyEnum.ASSET_TYPE]: [],
                [AssetOptionKeyEnum.USERS]: [],
                // NOTE: No endpoint to fetch from server, so we hardcode the static
                // values
                [AssetOptionKeyEnum.LIFECYCLE_STATUS]: OT_ASSET_DEFAULT_LIFECYCLE_STATUSES,
            },
            formFieldError: {
                name: '',
            },
            formSubmitLoadingState: LoadingState.NotPopulated,
            formSubmitError: '',
        },
        dispatch: undefined,
    },

    assetUploadModal: {
        state: {
            uploadingStatus: ProcessingStatus.NOT_PROCESSING,
            file: null,
            serverMessage: {
                type: null,
                message: '',
            },
            isOpen: false,
            files: [],
            fetchLoadingState: LoadingState.NotPopulated,
            selectedLiveAssets: [],
        },
        dispatch: undefined,
    },
    page: {
        state: {
            locationID: null,
            isDisabledAddButton: false,
            isOpenAssetCreationForm: false,
        },
        dispatch: undefined,
    },
    otSystems: {
        state: {
            loadingState: LoadingState.NotPopulated,
            data: {},
            expandedSystemNames: [],
            reportUrl: null,
            showGenerateReportPopup: false,
            createReportError: '',
            isErrorOpen: false,
        },
        dispatch: undefined,
    },
    systemAssessmentModal: {
        state: {
            isOpen: false,
            submissionState: LoadingState.NotPopulated,
        },
        dispatch: undefined,
    },
}

export const LocationInventoryPageContext = createContext<LocationInventoryPageContext>(
    defaultLocationInventoryContextState,
)

export interface CreateOTAssetOptionReq {
    name: string
}

export type PotentialAssetCountCriteria = {
    behaviours?: string[]
    locations?: GuidType[] | null
    networks?: AssetNetworkType[]
    otBehaviours?: string[]
    otNetworks?: AssetNetworkType[]
    applyOtCriteria?: boolean
    blockStatuses?: BlockStatus[]
}

export function isOTAssetOptionKey(s: string): s is AssetOptionKeyEnum {
    return Object.values(AssetOptionKeyEnum).includes(s as AssetOptionKeyEnum)
}

export type AssetOptions = Record<AssetOptionKeyEnum, AssetOption[]>

export type AssetOptionsFromServer = Partial<AssetOptions>
